import { FormControlLabel, Grid, Radio, RadioGroup } from '@mui/material';
import { savePDF } from '@progress/kendo-react-pdf';
import JsBarcode from 'jsbarcode';
import QRCode from 'qrcode';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import logo from 'src/assets/images/logos/washingtonmills_logo.png';
import { PrimaryButton } from 'src/components/shared/Button/Button';
import { Header4 } from 'src/components/shared/Typography/Header4';
import { ProductDetailDto } from 'src/store/products/product';
import styled from 'styled-components';
import { CommonLabelBody } from './CommonLabelBody';
import { EagLabelBody } from './EagLabelBody';
import { LotLabelBody } from './LotLabelBody';
import { NorlabLabelBody } from './NorlabLabelBody';
import { RunLabelBody } from './RunLabelBody';

type StandardLabelProps = {
  product: ProductDetailDto;
};

export type StandardLabelType = 'RUN' | 'LOT' | 'NORLAB' | 'EAG' | 'COMMON';

export type ScanType = 'barcode' | 'qrcode';

export const ScanType = {
  Barcode: 'barcode' as ScanType,
  QRcode: 'qrcode' as ScanType
};

export const StandardLabel: React.FC<StandardLabelProps> = ({ product }) => {
  const { t } = useTranslation();

  const [labelType, setLabelType] = useState<StandardLabelType>('RUN');
  const [scanType, setScanType] = useState<ScanType>(ScanType.Barcode);
  const [isPrinting, setIsPrinting] = useState<boolean>(false);

  const labelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const map: { [key in StandardLabelType]: string | number | undefined } = {
      RUN: product.runNo,
      LOT: product.lotNo,
      NORLAB: product.norlabNo,
      EAG: product.runSystem.no + '-' + product.runNo,
      COMMON: product.runNo
    };

    if (map[labelType] && scanType === ScanType.Barcode) {
      JsBarcode('#barcode', map[labelType]!.toString(), {
        displayValue: false,
        height: 80,
        format: 'code39'
      });
    }

    if (map[labelType] && scanType === ScanType.QRcode) {
      QRCode.toCanvas(
        document.getElementById('qrcode'),
        map[labelType]!.toString(),
        {
          width: 100,
          margin: 2
        }
      );
    }
  }, [product, labelType, scanType]);

  const onPrint = () => {
    setIsPrinting(true);
  };

  useEffect(() => {
    if (isPrinting) {
      const filenameMap: {
        [key in StandardLabelType]: string;
      } = {
        RUN: 'RunLabel.pdf',
        LOT: 'LotLabel.pdf',
        NORLAB: 'NorlabLabel.pdf',
        EAG: 'EagLabel.pdf',
        COMMON: 'Label.pdf'
      };

      savePDF(labelRef.current as HTMLElement, {
        fileName: filenameMap[labelType]
      });
      setIsPrinting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPrinting]);

  const getLabelBody = (product: ProductDetailDto, type: StandardLabelType) =>
    ({
      RUN: <RunLabelBody product={product} />,
      LOT: <LotLabelBody product={product} />,
      NORLAB: <NorlabLabelBody product={product} />,
      EAG: <EagLabelBody product={product} />,
      COMMON: <CommonLabelBody product={product} />
    }[type]);

  return (
    <Grid container>
      <Grid item xs={8}>
        <LabelWrapper ref={labelRef} className={isPrinting ? 'printing' : ''}>
          <LabelHeader>
            <img src={logo} alt="LIMS logo" />
          </LabelHeader>

          <LabelBody>{getLabelBody(product, labelType)}</LabelBody>

          <LabelFooter>
            {scanType === ScanType.Barcode ? (
              <canvas
                id="barcode"
                style={{ width: '100%', height: 'auto' }}
              ></canvas>
            ) : (
              <canvas id="qrcode"></canvas>
            )}
          </LabelFooter>
        </LabelWrapper>
      </Grid>
      <Grid item xs={4}>
        <Header4 className="mb-8">Type</Header4>
        <RadioGroup
          aria-label="label-type"
          value={labelType}
          onChange={event =>
            setLabelType(event.target.value as StandardLabelType)
          }
        >
          <FormControlLabel
            value={'RUN'}
            control={<Radio />}
            disabled={!product.runNo}
            label={t('product.run')}
          />
          <FormControlLabel
            value={'LOT'}
            control={<Radio />}
            disabled={!product.lotNo}
            label={t('product.lot')}
          />
          <FormControlLabel
            value={'NORLAB'}
            control={<Radio />}
            disabled={!product.norlabNo}
            label={t('product.norlab')}
          />
          <FormControlLabel
            value={'EAG'}
            control={<Radio />}
            disabled={!product.runNo}
            label={t('product.eag')}
          />
          <FormControlLabel
            value={'COMMON'}
            control={<Radio />}
            disabled={!product.runNo}
            label={t('label.common')}
          />
        </RadioGroup>

        <hr />

        <Header4 className="mb-8">Scan</Header4>
        <RadioGroup
          aria-label="scan-type"
          value={scanType}
          onChange={event => setScanType(event.target.value as ScanType)}
          className="mb-32"
        >
          <FormControlLabel
            value={ScanType.Barcode}
            control={<Radio />}
            label={t('barcode')}
          />
          <FormControlLabel
            value={ScanType.QRcode}
            control={<Radio />}
            label={t('qrcode')}
          />
        </RadioGroup>

        <PrimaryButton onClick={onPrint}>{t('print')}</PrimaryButton>
      </Grid>
    </Grid>
  );
};

const LabelWrapper = styled.div`
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  box-shadow: 0 5px 10px 0 rgba(0, 0, 0, 0.3);
  background-color: #fff;
  color: #333;
  width: 54mm;
  height: 101mm;
  padding: 20px 5px 20px 5px;

  &.printing textarea {
    border: 0;
    resize: none;
  }
`;

const LabelHeader = styled.div`
  margin-bottom: 8px;

  img {
    width: 100%;
  }
`;

export const LabelBody = styled.div`
  flex-grow: 1;

  p {
    padding: 0;
    margin: 0;

    label {
      display: inline-block;
      width: 60px;
      white-space: nowrap;
    }

    span {
      font-weight: bold;
    }
  }

  textarea {
    width: 100%;
    margin-top: 8px;
    font-size: 0.75rem;
    word-wrap: break-word;
  }

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
    margin-top: 8px;

    li {
      display: flex;
      align-items: center;
      padding: 2px 0;

      input {
        width: 16;
        height: 16;
        margin-right: 5px;
      }
    }
  }
`;

const LabelFooter = styled.div`
  display: flex;
  justify-content: center;
`;
